/**
 * JobChat.jsx
 * Path: frontend/src/components/JobChat.jsx
 * Description: Rozszerzony komunikator zlecenia z odpowiedziami, wzmiankami, edycją i multimediami.
 * Related: pages/JobDetailPage.jsx, api/jobs.js
 */

import { useMemo, useRef, useState } from "react";
import { AtSign, CornerUpLeft, ImagePlus, Pencil, Send, X } from "lucide-react";
import { toPolishDateTime } from "../utils/date";
import AttachmentPreviewModal from "./AttachmentPreviewModal";
import AttachmentTile from "./AttachmentTile";

function extractMentionTokens(text) {
  return Array.from(new Set((String(text || "").match(/@([A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}|[A-Za-z0-9._-]{2,})/g) || [])
    .map((item) => item.replace(/^@/, ""))));
}

function getMentionContext(text, caret) {
  const uptoCaret = String(text || "").slice(0, caret);
  const match = uptoCaret.match(/(^|\s)@([A-Za-z0-9._-]*)$/);
  if (!match) {
    return null;
  }

  return {
    query: match[2] || "",
    start: caret - match[2].length - 1,
    end: caret,
  };
}

function canEditMessage(message, user) {
  const role = String(user?.role || "").toLowerCase();
  if (role === "admin") {
    return true;
  }
  return String(message.authorId || "") === String(user?.id || "");
}

function renderMessageBody(text) {
  const value = String(text || "");
  if (!value) {
    return null;
  }

  const parts = value.split(/(@[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}|@[A-Za-z0-9._-]{2,})/g);
  return parts.map((part, index) => {
    if (!part) {
      return null;
    }
    if (part.startsWith("@")) {
      return (
        <span key={`${part}-${index}`} className="mention-pill-inline">
          {part}
        </span>
      );
    }
    return <span key={`${part}-${index}`}>{part}</span>;
  });
}

export default function JobChat({
  token,
  jobId,
  user,
  users,
  messages,
  sending,
  onSend,
  onEdit,
  onDownload,
}) {
  const textareaRef = useRef(null);
  const messageRefs = useRef({});

  const [draft, setDraft] = useState("");
  const [pendingFiles, setPendingFiles] = useState([]);
  const [replyDraft, setReplyDraft] = useState(null);
  const [editingMessage, setEditingMessage] = useState(null);
  const [mentionContext, setMentionContext] = useState(null);
  const [previewItem, setPreviewItem] = useState(null);

  const mentionSuggestions = useMemo(() => {
    if (!mentionContext) {
      return [];
    }

    const query = String(mentionContext.query || "").toLowerCase();
    return users
      .filter((entry) => {
        const email = String(entry.email || "").toLowerCase();
        const name = String(entry.name || "").toLowerCase();
        if (!query) {
          return true;
        }
        return email.includes(query) || name.includes(query);
      })
      .slice(0, 6);
  }, [mentionContext, users]);

  function focusComposer() {
    requestAnimationFrame(() => {
      textareaRef.current?.focus();
    });
  }

  function resetComposer() {
    setDraft("");
    setPendingFiles([]);
    setReplyDraft(null);
    setEditingMessage(null);
    setMentionContext(null);
  }

  function handleDraftChange(event) {
    const nextValue = event.target.value;
    setDraft(nextValue);
    setMentionContext(getMentionContext(nextValue, event.target.selectionStart || nextValue.length));
  }

  function handleDraftSelect(event) {
    setMentionContext(getMentionContext(draft, event.target.selectionStart || draft.length));
  }

  function handleInsertMention(entry) {
    const textarea = textareaRef.current;
    if (!textarea || !mentionContext) {
      return;
    }

    const mentionToken = `@${entry.email}`;
    const nextValue = `${draft.slice(0, mentionContext.start)}${mentionToken} ${draft.slice(mentionContext.end)}`;
    setDraft(nextValue);
    setMentionContext(null);

    requestAnimationFrame(() => {
      const nextCaret = mentionContext.start + mentionToken.length + 1;
      textarea.focus();
      textarea.setSelectionRange(nextCaret, nextCaret);
    });
  }

  function handleReply(message) {
    setReplyDraft({
      id: message.id,
      authorName: message.authorName,
      bodyPreview: message.body,
    });
    setEditingMessage(null);
    focusComposer();
  }

  function handleEdit(message) {
    setEditingMessage(message);
    setReplyDraft(null);
    setDraft(message.body || "");
    setPendingFiles([]);
    focusComposer();
  }

  function handlePendingFiles(event) {
    const files = Array.from(event.target.files || []);
    if (files.length > 0) {
      setPendingFiles((current) => [...current, ...files]);
    }
    event.target.value = "";
  }

  async function handleSubmit(event) {
    event.preventDefault();
    if (sending) {
      return;
    }

    const body = String(draft || "").trim();
    const mentions = extractMentionTokens(draft);

    if (editingMessage) {
      if (!body) {
        return;
      }
      await onEdit(editingMessage.id, { body, mentions });
      resetComposer();
      return;
    }

    if (!body && pendingFiles.length === 0) {
      return;
    }

    await onSend({
      body,
      mentions,
      replyToMessageId: replyDraft?.id || null,
      files: pendingFiles,
    });
    resetComposer();
  }

  return (
    <>
      <section className="card-section">
        <div className="section-header">
          <div>
            <h2>Komunikator</h2>
            <p>Szybkie ustalenia, odpowiedzi i multimedia w obrębie zlecenia.</p>
          </div>
        </div>

        <div className="chat-thread chat-thread-rich">
          {messages.length === 0 ? <div className="empty-state">Brak wiadomości.</div> : null}
          {messages.map((message) => (
            <article
              key={message.id}
              ref={(node) => {
                messageRefs.current[message.id] = node;
              }}
              className="chat-bubble chat-bubble-rich"
            >
              <div className="chat-bubble-head">
                <div className="chat-author-stack">
                  <strong>{message.authorName}</strong>
                  <span>{toPolishDateTime(message.createdAt)}</span>
                </div>
                <div className="chat-message-actions">
                  <button type="button" className="ghost-button chat-action-button" onClick={() => handleReply(message)}>
                    <CornerUpLeft size={14} />
                    Odpowiedz
                  </button>
                  {canEditMessage(message, user) ? (
                    <button type="button" className="ghost-button chat-action-button" onClick={() => handleEdit(message)}>
                      <Pencil size={14} />
                      Edytuj
                    </button>
                  ) : null}
                </div>
              </div>

              {message.meta?.replyTo ? (
                <button
                  type="button"
                  className="chat-reply-link"
                  onClick={() => messageRefs.current[message.meta.replyTo.messageId]?.scrollIntoView({ behavior: "smooth", block: "center" })}
                >
                  <span>{message.meta.replyTo.authorName || "Użytkownik"}</span>
                  <strong>{String(message.meta.replyTo.bodyPreview || "").slice(0, 120) || "Podgląd odpowiedzi"}</strong>
                </button>
              ) : null}

              {message.body ? <p className="chat-bubble-body">{renderMessageBody(message.body)}</p> : null}

              {Array.isArray(message.attachments) && message.attachments.length > 0 ? (
                <div className="message-attachment-grid">
                  {message.attachments.map((attachment) => (
                    <AttachmentTile
                      key={attachment.id}
                      token={token}
                      jobId={jobId}
                      attachment={attachment}
                      onDownload={onDownload}
                      onPreview={setPreviewItem}
                    />
                  ))}
                </div>
              ) : null}

              {message.isEdited ? <div className="chat-edited-flag">Edytowano</div> : null}
            </article>
          ))}
        </div>

        <form className="chat-form chat-form-rich" onSubmit={handleSubmit}>
          {replyDraft ? (
            <div className="chat-context-card">
              <div>
                <strong>Odpowiedź do: {replyDraft.authorName}</strong>
                <p>{String(replyDraft.bodyPreview || "").slice(0, 140)}</p>
              </div>
              <button type="button" className="ghost-button icon-button" onClick={() => setReplyDraft(null)} aria-label="Anuluj odpowiedź">
                <X size={16} />
              </button>
            </div>
          ) : null}

          {editingMessage ? (
            <div className="chat-context-card">
              <div>
                <strong>Edycja wiadomości</strong>
                <p>{editingMessage.authorName}</p>
              </div>
              <button type="button" className="ghost-button" onClick={resetComposer}>
                Anuluj edycję
              </button>
            </div>
          ) : null}

          {pendingFiles.length > 0 ? (
            <div className="pending-attachments">
              {pendingFiles.map((file, index) => (
                <div key={`${file.name}-${index}`} className="pending-attachment-chip">
                  <span>{file.name}</span>
                  <button
                    type="button"
                    className="ghost-button icon-button"
                    onClick={() => setPendingFiles((current) => current.filter((_, itemIndex) => itemIndex !== index))}
                    aria-label="Usuń plik"
                  >
                    <X size={14} />
                  </button>
                </div>
              ))}
            </div>
          ) : null}

          <div className="chat-compose-box">
            <textarea
              ref={textareaRef}
              rows={4}
              value={draft}
              onChange={handleDraftChange}
              onSelect={handleDraftSelect}
              placeholder="Napisz wiadomość. Użyj @ aby oznaczyć użytkownika."
            />

            {mentionSuggestions.length > 0 ? (
              <div className="mention-suggestions">
                {mentionSuggestions.map((entry) => (
                  <button type="button" key={entry.id} className="mention-suggestion-item" onClick={() => handleInsertMention(entry)}>
                    <AtSign size={14} />
                    <span>{entry.name || entry.email}</span>
                    <strong>{entry.email}</strong>
                  </button>
                ))}
              </div>
            ) : null}
          </div>

          <div className="chat-form-actions">
            {!editingMessage ? (
              <label className="ghost-button primary-button-file">
                <input type="file" accept="image/*,video/*,.pdf,.doc,.docx,.xls,.xlsx,.txt" multiple onChange={handlePendingFiles} />
                <ImagePlus size={16} />
                Multimedia
              </label>
            ) : null}
            <button type="submit" className="primary-button" disabled={sending}>
              <Send size={16} />
              {sending ? "Zapisywanie..." : editingMessage ? "Zapisz" : "Wyślij"}
            </button>
          </div>
        </form>
      </section>

      <AttachmentPreviewModal item={previewItem} onClose={() => setPreviewItem(null)} />
    </>
  );
}
